import { useHudStore, type HudStore } from "../store/hudStore";

// Real-URL routing for the HUD, without a router library. Each screen maps to a
// path (see navToPath/pathToNav); navigating pushes it, Back/Forward + refresh
// read it back. CloudFront serves index.html for any unknown path (the SPA
// fallback in web_hosting.tf), so a deep link like /quest/<id> loads and this
// module replays it once the data is in. The URL is the source of truth - no
// sessionStorage, and share/refresh/deep-link all work for free.

type Screen = HudStore["screen"];

// The store slice that identifies a distinct page - not transient UI like text
// drafts, the focus timer, or the vault search box.
type Nav = Pick<HudStore, "screen" | "questId" | "debtId" | "main" | "domain" | "attr" | "noteId" | "focusId" | "soonLabel">;

const navOf = (s: HudStore): Nav => ({
  screen: s.screen, questId: s.questId, debtId: s.debtId, main: s.main, domain: s.domain,
  attr: s.attr, noteId: s.noteId, focusId: s.focusId, soonLabel: s.soonLabel,
});

const sameNav = (a: Nav, b: Nav): boolean =>
  a.screen === b.screen && a.questId === b.questId && a.debtId === b.debtId && a.main === b.main &&
  a.domain === b.domain && a.attr === b.attr && a.noteId === b.noteId &&
  a.focusId === b.focusId && a.soonLabel === b.soonLabel;

// loading + login have no URL of their own - a refresh must never land on the
// spinner or the sign-in form, so boot/auth leave the address bar untouched.
const transient = (screen: Screen): boolean => screen === "loading" || screen === "login";

// store → path. Ids that may contain slashes/spaces (vault note keys, domains)
// ride in the query string so the path stays clean.
const navToPath = (n: Nav): string => {
  switch (n.screen) {
    case "quest":     return `/quest/${encodeURIComponent(n.questId ?? "")}`;
    case "debt":      return `/debt/${encodeURIComponent(n.debtId ?? "")}`;
    case "log":       return "/log";
    case "settings":  return "/settings";
    case "character": return n.attr ? `/character?attr=${encodeURIComponent(n.attr)}` : "/character";
    case "vault":     return n.noteId ? `/vault?note=${encodeURIComponent(n.noteId)}` : "/vault";
    case "main":      return `/main/${encodeURIComponent(n.main ?? "")}`;
    case "domain":    return `/domain/${encodeURIComponent(n.domain ?? "")}`;
    case "focus":     return `/focus/${encodeURIComponent(n.focusId ?? "")}`;
    case "soon":      return n.soonLabel ? `/soon?label=${encodeURIComponent(n.soonLabel)}` : "/soon";
    default:          return "/"; // hud
  }
};

// path → store patch. Unknown paths fall back to the HUD.
const pathToNav = (pathname: string, search: string): Partial<HudStore> & { screen: Screen } => {
  const p = new URLSearchParams(search);
  const parts = pathname.replace(/^\/+|\/+$/g, "").split("/");
  const head = parts[0] ?? "";
  let arg = "";
  try { arg = parts[1] ? decodeURIComponent(parts[1]) : ""; } catch { return { screen: "hud" }; }
  switch (head) {
    case "quest":     return { screen: "quest", questId: arg || null };
    case "debt":      return { screen: "debt", debtId: arg || null };
    case "log":       return { screen: "log" };
    case "settings":  return { screen: "settings" };
    case "character": return { screen: "character", attr: p.get("attr") };
    case "vault":     return { screen: "vault", noteId: p.get("note") };
    case "main":      return { screen: "main", main: arg || "m1" };
    case "domain":    return { screen: "domain", domain: arg };
    case "focus":     return { screen: "focus", focusId: arg || null };
    case "soon":      return { screen: "soon", soonLabel: p.get("label") ?? "" };
    default:          return { screen: "hud" };
  }
};

// A path is only openable if its data is present - never a quest/debt that has
// been deleted (or was never loaded). Other screens render from whatever's in
// the store.
const openable = (nav: Partial<HudStore> & { screen: Screen }, s: HudStore): boolean => {
  if (nav.screen === "quest") return !!nav.questId && s.quests.some((q) => q.id === nav.questId);
  if (nav.screen === "debt") return !!nav.debtId && s.debts.some((d) => d.id === nav.debtId);
  return true;
};

let restoring = false; // true while syncing store←URL (boot/popstate), so it doesn't re-push

export function initHistorySync(): void {
  // The address the tab loaded at - replayed once boot reaches the HUD (so a deep
  // link survives the loading→login→hud detour). Captured now, before boot/auth
  // can rewrite the URL (auth.ts strips the OAuth ?code).
  let pending: (Partial<HudStore> & { screen: Screen }) | null = pathToNav(location.pathname, location.search);
  if (pending.screen === "hud" || transient(pending.screen)) pending = null;
  let booted = false;

  const applyUrl = (): void => {
    const state = useHudStore.getState();
    if (!state.live || !sessionStorage.getItem("access_token")) {
      if (state.screen !== "loading") useHudStore.setState({ screen: "login" });
      history.replaceState(null, "", "/");
      pending = null;
      return;
    }
    const nav = pathToNav(location.pathname, location.search);
    restoring = true;
    if (openable(nav, useHudStore.getState())) useHudStore.setState(nav);
    else { useHudStore.setState({ screen: "hud" }); history.replaceState(null, "", "/"); }
    restoring = false;
  };

  useHudStore.subscribe((state, prev) => {
    const cur = navOf(state);

    // Boot settled on the HUD (goLive loads quests + vault before flipping to
    // "hud") → replay the deep link the tab loaded at, then we're live.
    if (!booted && cur.screen === "hud" && transient(prev.screen)) {
      booted = true;
      if (pending) {
        const target = pending; pending = null;
        restoring = true;
        if (openable(target, state)) { useHudStore.setState(target); history.replaceState(null, "", navToPath(target as Nav)); }
        else history.replaceState(null, "", "/");
        restoring = false;
      }
      return;
    }

    if (restoring) return;
    if (sameNav(cur, navOf(prev))) return;

    if (transient(cur.screen)) {
      // Explicit sign-out (real screen → login) → clean the address bar. Boot
      // landing on login (from "loading") is left alone so the deep link holds.
      if (cur.screen === "login" && !transient(prev.screen)) { pending = null; history.replaceState(null, "", "/"); }
      return;
    }
    const path = navToPath(cur);
    if (path === location.pathname + location.search) return; // already there (URL led)
    if (transient(prev.screen)) history.replaceState(null, "", path);
    else history.pushState(null, "", path);
  });

  window.addEventListener("popstate", applyUrl);
}
